/**
 * Death, burial, and rebirth (M4-07).
 *
 * Decision log #2 lands here. True death is real — the corpse stays where it
 * fell with the gear on it (`death.ts`), and getting your kit back is an
 * expedition made naked — but nobody is deleted: the old Sun Stone south of
 * the village gives them back, *changed*, and the change stacks (`marks.ts`).
 *
 * This module owns the three things around that:
 *
 * - **Burial.** A grave is a permanent world object with a name, an epitaph
 *   carved from how they died, and whatever they were carrying. It is the one
 *   thing a friend can do for a body that is better than guarding it, and
 *   because it is permanent it is also the valley's memory of everybody who
 *   has ever died in it.
 * - **Robbery.** A grave can be dug open again. That is not an oversight: a
 *   world where burial were *safe* would be a world with no reason to trust
 *   anybody, and the whole design of M2-07's PvP is that trust is the
 *   mechanic. Digging one up is loud, slow, and locally broadcast.
 * - **Rebirth.** `rebirth` is the moment at the relic: a whole body, the old
 *   marks, and one new one. It is a pure function so the server can call it at
 *   the end of a shade-walk and a test can call it directly.
 *
 * The *shade-walk* itself is not here and should not be: it is sixty seconds of
 * a particular client seeing a particular thing, which is the server's business
 * (`world-session.ts`), and a sim system that held a player out of the world for
 * a minute would be a rule no replay could reproduce.
 */
import { capabilityFault } from './affordance.js';
import { chebyshev } from './actor.js';
import { parseBury, parseDig } from './actions.js';
import { createBody } from './body.js';
import { isHelpless } from './fighter.js';
import { achePain, bearMark, drawMark, houndDreamed } from './marks.js';
import { isPassable, tileAt } from './map.js';
import { TICKS_PER_SECOND } from './time.js';
import { createVitals } from './vitals.js';
import { daylight, isRaining } from './weather.js';
/** Digging a grave is most of a minute's work, and it is meant to be felt. */
export const BURY_TICKS = TICKS_PER_SECOND * 40;
/** Opening one is quicker than digging it. The ground is already broken. */
export const DIG_TICKS = TICKS_PER_SECOND * 25;
/** How far the sound of a spade carries. Robbery should have witnesses. */
export const GRAVE_EARSHOT = 20;
/* -------------------------------------------------------------------------- *
 * The epitaph
 * -------------------------------------------------------------------------- */
/**
 * One line, carved from how they died.
 *
 * Composed here rather than authored per cause in content, because it is a
 * sentence assembled from *this* death's facts — the name, the cause, and the
 * place — and a content table of five strings would say less. It is the same
 * argument `wound.ts` makes about provenance: the record is the story.
 */
export function epitaphFor(corpse) {
    const how = {
        wound: 'died of their wounds',
        blood: 'bled to death',
        infection: 'died of the rot',
        execution: 'was killed where they lay',
    };
    return `${corpse.name}, who ${how[corpse.cause]}.`;
}
/* -------------------------------------------------------------------------- *
 * Burial
 * -------------------------------------------------------------------------- */
/** The corpse within reach, or `null`. Nearest wins; ties break on the id. */
export function corpseInReach(world, actor) {
    let best = null;
    for (const id of Object.keys(world.corpses).sort()) {
        const corpse = world.corpses[id];
        if (!corpse)
            continue;
        if (chebyshev(actor.x, actor.y, corpse.x, corpse.y) > 1)
            continue;
        if (!best ||
            chebyshev(actor.x, actor.y, corpse.x, corpse.y) < chebyshev(actor.x, actor.y, best.x, best.y)) {
            best = corpse;
        }
    }
    return best;
}
/** The grave within reach that still has somebody in it. */
export function graveInReach(world, actor) {
    for (const id of Object.keys(world.graves ?? {}).sort()) {
        const grave = world.graves[id];
        if (!grave || grave.robbed)
            continue;
        if (chebyshev(actor.x, actor.y, grave.x, grave.y) <= 1)
            return grave;
    }
    return null;
}
/** Why there is nothing to bury, or `null`. */
export function buryFault(world, catalogue, actor, tick) {
    const digger = world.combat[actor.id];
    if (digger && isHelpless(catalogue, digger, tick))
        return 'you are in no state to';
    const capFault = capabilityFault(catalogue, digger, 'bury');
    if (capFault)
        return capFault;
    return corpseInReach(world, actor) ? null : 'there is nobody here to bury';
}
/** Why there is no grave to open, or `null`. */
export function digFault(world, catalogue, actor, tick) {
    const digger = world.combat[actor.id];
    if (digger && isHelpless(catalogue, digger, tick))
        return 'you are in no state to';
    const capFault = capabilityFault(catalogue, digger, 'dig');
    if (capFault)
        return capFault;
    return graveInReach(world, actor) ? null : 'there is no grave here to open';
}
/**
 * Put them in the ground.
 *
 * The corpse leaves the world and a grave takes its place, carrying the gear.
 * Broadcast to witnesses rather than to earshot: a burial is a thing you do in
 * front of people, and the log is the only funeral this game has.
 */
export function finishBury(ctx, actor) {
    const corpse = corpseInReach(ctx.world, actor);
    if (!corpse)
        return;
    const grave = {
        id: `grave:${corpse.ownerId}:${ctx.tick}`,
        x: corpse.x,
        y: corpse.y,
        ownerId: corpse.ownerId,
        name: corpse.name,
        buriedBy: actor.name,
        buriedAt: ctx.tick,
        cause: corpse.cause,
        epitaph: epitaphFor(corpse),
        loadout: corpse.loadout,
        robbed: false,
    };
    ctx.world.graves[grave.id] = grave;
    delete ctx.world.corpses[corpse.id];
    ctx.emit({
        type: 'died',
        actors: [actor.id, corpse.ownerId],
        refs: ['bury', 'done', grave.id],
        text: `${actor.name} covers ${corpse.name} over, and sets a stone at the head of it.`,
        audience: 'witnesses',
        at: { x: grave.x, y: grave.y },
    });
}
/**
 * Open one again.
 *
 * The body comes back out as a corpse, which is the tidy answer and the honest
 * one: whatever was buried with them is on it, and taking it is `loot`'s
 * business — the same verb, the same ten seconds, the same witnesses. What this
 * verb does is make the gear *reachable*, and say loudly that somebody did it.
 */
export function finishDig(ctx, actor) {
    const grave = graveInReach(ctx.world, actor);
    if (!grave)
        return;
    grave.robbed = true;
    const corpseId = `corpse:${grave.ownerId}`;
    ctx.world.corpses[corpseId] = {
        id: corpseId,
        x: grave.x,
        y: grave.y,
        ownerId: grave.ownerId,
        name: grave.name,
        loadout: grave.loadout,
        // The body as the grave held it: unmarked by the digging, and dead.
        // The body as the grave held it. When its owner is still in the world —
        // reborn and standing at the village relic — their *current* body is the wrong
        // one to copy, so this takes a fresh one of the same plan: what the robber
        // is after is the gear, and a corpse's wounds are already written in the
        // log they came from.
        vitals: createVitals(createBody(planOf(ctx.catalogue, grave.loadout.planId))),
        diedAt: grave.buriedAt,
        cause: grave.cause,
    };
    ctx.emit({
        type: 'died',
        actors: [actor.id, grave.ownerId],
        refs: ['dig', 'robbed', grave.id],
        // Named, and loudly. A crime nobody could have witnessed is not a crime the
        // valley can do anything about, and this game's only sanction is memory.
        text: `${actor.name} digs ${grave.name} up again.`,
        audience: 'earshot',
        range: GRAVE_EARSHOT,
        at: { x: grave.x, y: grave.y },
    });
}
/** The plan a buried loadout names, or a bare one for content that has moved on. */
function planOf(catalogue, planId) {
    return catalogue.plan(planId) ?? { id: planId, name: planId, parts: [] };
}
/** What examining a grave reads as. Composed, like `describeCorpse`. */
export function describeGrave(grave) {
    const marker = grave.robbed
        ? 'The grave has been dug open and left that way.'
        : 'A low mound with a stone set at the head of it.';
    return `${marker} ${grave.epitaph} Buried by ${grave.buriedBy}.`;
}
/* -------------------------------------------------------------------------- *
 * The cauldron
 * -------------------------------------------------------------------------- */
/** Where the Pair Dadeni stands, or `null` in a world whose map has none. */
export function cauldronAt(map) {
    for (let y = 0; y < map.height; y += 1) {
        for (let x = 0; x < map.width; x += 1) {
            if (tileAt(map, x, y) === 'cauldron')
                return { x, y };
        }
    }
    return null;
}
/** Where Llan-y-Nant's pagan rebirth relic stands, or `null` on an older map. */
export function rebirthRelicAt(map) {
    for (let y = 0; y < map.height; y += 1) {
        for (let x = 0; x < map.width; x += 1) {
            if (tileAt(map, x, y) === 'sun-stone')
                return { x, y };
        }
    }
    return null;
}
/**
 * Where somebody actually wakes up (M4-07).
 *
 * The Sun Stone is a flagstone made to be stood upon, so the dead return
 * directly onto the symbol at the centre of the village ring. A pre-relic map
 * can still fall back to the old Pair Dadeni placement beside its impassable
 * cauldron; `hydrateWorld` normally upgrades such a live save first.
 *
 * `null` only when neither landmark offers a safe place. The join intent then
 * uses the world's ordinary village spawn, so a malformed map cannot strand a
 * dead player outside the world.
 */
export function rebirthPlace(map) {
    const relic = rebirthRelicAt(map);
    if (relic && isPassable(map, relic.x, relic.y))
        return relic;
    const stone = cauldronAt(map);
    if (!stone)
        return null;
    for (const [dx, dy] of [
        [0, 1],
        [0, -1],
        [1, 0],
        [-1, 0],
        [1, 1],
        [-1, 1],
        [1, -1],
        [-1, -1],
    ]) {
        const x = stone.x + dx;
        const y = stone.y + dy;
        if (isPassable(map, x, y))
            return { x, y };
    }
    return null;
}
/**
 * What comes out of the cauldron (M4-07).
 *
 * A **whole** body — no wounds, no fever, full blood — carrying every mark the
 * old one bore plus one new one. Whole is the point: the bargain is not "you
 * come back hurt", it is "you come back *different*", and a returned character
 * who also had to convalesce would be a character nobody wants to play.
 *
 * Pure, and takes the body it is replacing rather than reading world state, so
 * that the server can call it between ticks and a test can call it with nothing
 * but a `Vitals` and a seeded `Rng`.
 */
export function rebirth(catalogue, rng, previous) {
    const vitals = createVitals(previous.body);
    // Every part comes back sound: the cauldron gives the body back, and a
    // severed leg that stayed severed would make the second death permanent in a
    // way the marks deliberately are not.
    for (const state of Object.values(vitals.body.parts)) {
        state.severed = false;
        state.destroyed = false;
        state.broken = false;
        state.blind = false;
        state.impairment = 0;
    }
    vitals.marks = [...(previous.marks ?? [])];
    const drawn = drawMark(catalogue, rng, vitals);
    if (drawn)
        bearMark(vitals, drawn.id);
    return { vitals, mark: drawn?.id ?? null };
}
/* -------------------------------------------------------------------------- *
 * The hounds (M4-07's Hound-Dreamed)
 * -------------------------------------------------------------------------- */
/** How often the hounds are heard, per hour of darkness. */
export const HOUND_CHANCE = 0.35;
/** How far a hound-dreamer's warning reaches: what is coming, and roughly where. */
export const HOUND_SIGHT = 20;
const NIGHT_DAYLIGHT = 0.3;
/**
 * The Cŵn Annwn, for those who have heard them from the other side.
 *
 * Hourly, at night, and only for the marked. Most nights it is flavour — the
 * hounds are out, and that is all. When something hostile is genuinely inside
 * `HOUND_SIGHT`, the line says so without saying what or exactly where, which
 * is the shape a *true warning* should have: enough to make you look up, never
 * enough to replace looking.
 */
export const houndSystem = {
    name: 'hounds',
    phase: 'hour',
    run(ctx) {
        if (daylight(ctx.time) >= NIGHT_DAYLIGHT)
            return;
        for (const id of Object.keys(ctx.world.combat).sort()) {
            const combatant = ctx.world.combat[id];
            const actor = ctx.world.actors[id];
            if (!combatant || !actor)
                continue;
            if (!houndDreamed(ctx.catalogue, combatant.vitals))
                continue;
            if (!ctx.rng(`hounds:${id}`).chance(HOUND_CHANCE))
                continue;
            const near = hostileNear(ctx, actor);
            ctx.emit({
                type: 'status',
                actors: [id],
                refs: ['hounds', near ? 'warning' : 'dream'],
                text: near
                    ? 'The hounds are loud tonight, and they are not running away from anything.'
                    : 'Somewhere off the moor, white hounds are running. You know the sound now.',
                audience: 'actor',
                to: id,
                at: { x: actor.x, y: actor.y },
            });
        }
    },
};
/** Is there something with teeth inside the hounds' reach? */
function hostileNear(ctx, actor) {
    for (const other of Object.values(ctx.world.actors)) {
        if (other.id === actor.id || other.kind !== 'creature')
            continue;
        if (chebyshev(actor.x, actor.y, other.x, other.y) > HOUND_SIGHT)
            continue;
        const state = ctx.world.minds[other.id]?.state;
        if (state === 'stalk' || state === 'fight')
            return true;
    }
    return false;
}
/* -------------------------------------------------------------------------- *
 * The ache (M4-07's Old-Ache)
 * -------------------------------------------------------------------------- */
/**
 * Write the weather onto the marked, once an hour.
 *
 * A system rather than a term inside `effectivePool` for the reason the top of
 * this file gives about the shade-walk: the fight rules know nothing about the
 * sky and the mark table, and threading both into a function called on every
 * exchange to serve one point of pain would be the wrong trade. What lands on
 * the body is the consequence — `vitals.ache` — exactly as hunger lands as a
 * band and infection lands as a fever.
 */
export const acheSystem = {
    name: 'aches',
    phase: 'hour',
    run(ctx) {
        const raining = isRaining(ctx.world.weather.kind);
        for (const id of Object.keys(ctx.world.combat).sort()) {
            const combatant = ctx.world.combat[id];
            if (!combatant)
                continue;
            const before = combatant.vitals.ache ?? 0;
            const now = achePain(ctx.catalogue, combatant.vitals, raining);
            if (now === before)
                continue;
            combatant.vitals.ache = now;
            const actor = ctx.world.actors[id];
            if (!actor)
                continue;
            ctx.emit({
                type: 'status',
                actors: [id],
                refs: ['ache', now > before ? 'worse' : 'easing'],
                text: now > before
                    ? 'The wet gets into the old places, and they let you know.'
                    : 'The ache lets go of you a little as the weather lifts.',
                audience: 'actor',
                to: id,
                at: { x: actor.x, y: actor.y },
            });
        }
    },
};
/* -------------------------------------------------------------------------- *
 * Handlers
 * -------------------------------------------------------------------------- */
function digging(type, parse, fault, begin) {
    return {
        type,
        parse,
        check(intent, ctx) {
            const actor = ctx.world.actors[intent.actorId];
            if (!actor)
                return `${intent.actorId} is not in the world`;
            return fault(ctx.world, ctx.catalogue, actor, ctx.tick);
        },
        apply(intent, ctx) {
            const actor = ctx.world.actors[intent.actorId];
            const combatant = ctx.world.combat[intent.actorId];
            if (!actor || !combatant)
                return;
            begin(ctx, actor, combatant);
        },
    };
}
export const buryHandler = digging('bury', parseBury, buryFault, (ctx, actor, combatant) => {
    openGrave(ctx, actor, combatant, 'bury');
});
export const digHandler = digging('dig', parseDig, digFault, (ctx, actor, combatant) => {
    openGrave(ctx, actor, combatant, 'dig');
});
/** Start the digging. A ground channel, like foraging: it works on a place. */
function openGrave(ctx, actor, combatant, verb) {
    combatant.channel = {
        verb,
        targetId: null,
        startedAt: ctx.tick,
        completesAt: ctx.tick + (verb === 'bury' ? BURY_TICKS : DIG_TICKS),
        fromX: actor.x,
        fromY: actor.y,
        detail: null,
    };
    ctx.emit({
        type: 'interacted',
        actors: [actor.id],
        refs: [verb, 'begun'],
        text: verb === 'bury'
            ? `${actor.name} begins to dig a grave.`
            : `${actor.name} sets a spade to a grave.`,
        audience: 'witnesses',
        at: { x: actor.x, y: actor.y },
    });
}
export const CAULDRON_INTENTS = [
    buryHandler,
    digHandler,
];
//# sourceMappingURL=cauldron.js.map